import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import {
  IGeofenceZone,
  IGeofenceEvent,
  IGeofenceMoveRequest,
  ISaveGeofenceZoneRequest,
  IGeofenceGeometry,
} from '../interfaces/heavyDutyMachinery/IGeofence';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/**
 * The work zone for one booking. The host writes it, both sides read it --
 * being able to be accused of crossing a line without being allowed to see
 * where the line is would be indefensible.
 */

/**
 * Undefined means there is genuinely nothing to show: no zone AND no
 * drop-off point to suggest one around (a self-pickup booking). When there
 * IS a drop-off point the backend answers with a suggestion instead of a
 * 404, so the host opens the map on the site rather than on the ocean --
 * check `isSuggestion` before treating it as saved.
 */
export const getGeofenceZone = async (bookingId: string): Promise<IGeofenceZone | undefined> => {
  const response = await apiService<IGeofenceZone>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_ZONE(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  return response.success ? response.data : undefined;
};

/** Host only. Replaces whatever was there -- redrawing is the same call. */
export const saveGeofenceZone = async (bookingId: string, zone: ISaveGeofenceZoneRequest): Promise<IGeofenceZone> => {
  const response = await apiService<IGeofenceZone>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_ZONE(bookingId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...zone },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the work zone');
};

export const getGeofenceEvents = async (bookingId: string): Promise<IGeofenceEvent[]> => {
  const response = await apiService<IGeofenceEvent[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_EVENTS(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  return response.success ? response.data : [];
};

export const getGeofenceMoveRequests = async (bookingId: string): Promise<IGeofenceMoveRequest[]> => {
  const response = await apiService<IGeofenceMoveRequest[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_MOVE_REQUESTS(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  return response.success ? response.data : [];
};

/**
 * Guest only -- asking to work somewhere else, which the host then agrees to
 * or doesn't. Carries a whole shape rather than a point, because approving it
 * replaces the zone with exactly this.
 */
export const requestGeofenceMove = async (
  bookingId: string,
  geometry: IGeofenceGeometry,
  reason: string,
): Promise<IGeofenceMoveRequest> => {
  const response = await apiService<IGeofenceMoveRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_MOVE_REQUESTS(bookingId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...geometry, reason },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to request the move');
};

/** Host only. Approving moves the zone and closes any open breach in the same call. */
export const decideGeofenceMove = async (
  bookingId: string,
  id: string,
  approve: boolean,
  reason?: string,
): Promise<IGeofenceMoveRequest> => {
  const response = await apiService<IGeofenceMoveRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_GEOFENCE_MOVE_DECIDE(bookingId, id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { approve, reason },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to decide the move request');
};
